import React from "react";
import {
  Box,
  Divider,
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
  useBreakpointValue,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaCheckCircle } from "react-icons/fa";

interface VerifyCertificateModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipientName?: string;
  programName?: string;
  issueDate?: string;
  certificateId?: string;
  issuedBy?: string;
}

const DetailRow: React.FC<{ label: string; value?: string }> = ({ label, value }) => {
  const labelColor = useColorModeValue("gray.500", "gray.400");
  if (!value) return null;
  return (
    <Box w="100%">
      <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color={labelColor} letterSpacing="wide">
        {label}
      </Text>
      <Text fontSize="md" fontWeight="medium" wordBreak="break-all">
        {value}
      </Text>
    </Box>
  );
};

export const VerifyCertificateModal: React.FC<VerifyCertificateModalProps> = ({
  isOpen,
  onClose,
  recipientName,
  programName,
  issueDate,
  certificateId,
  issuedBy,
}) => {
  const bannerBg = useColorModeValue("green.50", "green.900");
  const bannerColor = useColorModeValue("green.600", "green.200");
  const modalSize = useBreakpointValue({ base: "full", md: "md" });

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size={modalSize}>
      <ModalOverlay />
      <ModalContent mx={{ base: 0, md: 4 }}>
        <ModalCloseButton />
        <ModalBody pt={8} pb={6}>
          <Flex direction="column" align="center" bg={bannerBg} rounded="lg" p={5} mb={5}>
            <Icon as={FaCheckCircle} boxSize={12} color={bannerColor} mb={3} />
            <Text fontSize="xl" fontWeight="bold" color={bannerColor}>
              Credential Verified
            </Text>
            <Text fontSize="sm" textAlign="center" mt={1}>
              This credential has been successfully verified
              {recipientName ? (
                <>
                  {" "}for <Text as="span" fontWeight="bold">{recipientName}</Text>
                </>
              ) : null}
              .
            </Text>
          </Flex>
          <VStack spacing={3} align="stretch">
            <DetailRow label="Recipient" value={recipientName} />
            <DetailRow label="Program" value={programName} />
            <DetailRow label="Issue Date" value={issueDate} />
            <DetailRow label="Issued By" value={issuedBy} />
            <Divider />
            <DetailRow label="Certificate ID" value={certificateId} />
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
